import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReportPreview = ({ reportData, template }) => {
  const [previewMode, setPreviewMode] = useState('formatted');
  const [zoom, setZoom] = useState(100);

  const mockReportData = {
    title: 'Ransomware Investigation Report - Case #2025-001',
    investigator: 'Agent Sarah Johnson',
    caseNumber: 'CRYPTO-2025-001',
    dateGenerated: '2025-01-15',
    classification: 'Confidential',
    sections: {
      'executive-summary': {
        title: 'Executive Summary',
        content: `This report documents the investigation of a ransomware attack that occurred on January 10, 2025, targeting XYZ Corporation. The attackers demanded 2.5 BTC (approximately $105,000 USD) in ransom payment.\n\nOur investigation successfully traced the cryptocurrency payments through multiple wallet addresses and identified the final destination of the funds. Key findings include the use of mixing services, multiple intermediate addresses, and eventual deposit to a major cryptocurrency exchange.\n\nThe investigation provides actionable intelligence for law enforcement and demonstrates clear evidence of the fund flow from victim to perpetrator.`
      },
      'attack-timeline': {
        title: 'Attack Timeline',
        content: `January 10, 2025 - 09:15 UTC: Initial system compromise detected\nJanuary 10, 2025 - 09:45 UTC: Ransomware deployment across network\nJanuary 10, 2025 - 10:30 UTC: Ransom note displayed on infected systems\nJanuary 10, 2025 - 14:30 UTC: First ransom payment of 2.5 BTC made\nJanuary 10, 2025 - 15:45 UTC: Funds moved to intermediate address\nJanuary 11, 2025 - 08:20 UTC: Funds processed through mixing service\nJanuary 12, 2025 - 16:10 UTC: Final deposit to exchange detected`
      }
    }
  };

  const currentData = { ...mockReportData, ...reportData };

  const renderFormattedPreview = () => (
    <div className="bg-white p-8 shadow-forensic-modal rounded-lg" style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left' }}>
      {/* Report Header */}
      <div className="border-b-2 border-gray-300 pb-6 mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {currentData?.title || 'Forensic Investigation Report'}
            </h1>
            <div className="space-y-1 text-sm text-gray-600">
              <p><strong>Case Number:</strong> {currentData?.caseNumber || 'N/A'}</p>
              <p><strong>Investigator:</strong> {currentData?.investigator || 'N/A'}</p>
              <p><strong>Date Generated:</strong> {currentData?.dateGenerated || new Date()?.toLocaleDateString()}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="bg-red-100 text-red-800 px-3 py-1 rounded text-sm font-medium">
              {currentData?.classification || 'CONFIDENTIAL'}
            </div>
            <div className="mt-2 text-xs text-gray-500">
              CryptoTrace Forensic Intelligence
            </div>
          </div>
        </div>
      </div>

      {/* Table of Contents */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Table of Contents</h2>
        <div className="space-y-2">
          {Object.entries(currentData?.sections || {})?.map(([key, section], index) => (
            <div key={key} className="flex justify-between border-b border-dotted border-gray-300 pb-1">
              <span className="text-sm">{index + 1}. {section?.title}</span>
              <span className="text-sm text-gray-500">{index + 2}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Report Sections */}
      {Object.entries(currentData?.sections || {})?.map(([key, section], index) => (
        <div key={key} className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
            {index + 1}. {section?.title}
          </h2>
          <div className="prose prose-sm max-w-none">
            {section?.content?.split('\n')?.map((paragraph, pIndex) => (
              <p key={pIndex} className="mb-3 text-gray-700 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>
          
          {/* Sample Evidence Section */}
          {key === 'attack-timeline' && (
            <div className="mt-6 bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">Supporting Evidence</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <strong>Transaction Hash:</strong><br />
                  <code className="text-xs bg-gray-200 px-1 rounded">1a2b3c4d5e6f7g8h...</code>
                </div>
                <div>
                  <strong>Amount:</strong><br />
                  2.5 BTC ($105,000 USD)
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Footer */}
      <div className="border-t-2 border-gray-300 pt-6 mt-12">
        <div className="flex justify-between items-center text-sm text-gray-600">
          <div>
            <p>Generated by CryptoTrace Forensic Intelligence Platform</p>
            <p>Report ID: {Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase()}</p>
          </div>
          <div className="text-right">
            <p>Page 1 of 1</p>
            <p>{new Date()?.toLocaleDateString()} - {new Date()?.toLocaleTimeString()}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRawPreview = () => (
    <div className="bg-gray-900 text-green-400 p-6 rounded-lg font-mono text-sm">
      <pre className="whitespace-pre-wrap">
{`FORENSIC INVESTIGATION REPORT
=============================

Case Number: ${currentData?.caseNumber || 'N/A'}
Investigator: ${currentData?.investigator || 'N/A'}
Date: ${currentData?.dateGenerated || new Date()?.toLocaleDateString()}
Classification: ${currentData?.classification || 'CONFIDENTIAL'}

${Object.entries(currentData?.sections || {})?.map(([key, section]) => `
${section?.title?.toUpperCase()}
${'='?.repeat(section?.title?.length)}

${section?.content || 'No content available'}
`)?.join('\n')}

---
Generated by CryptoTrace v2.1.0
Report Hash: ${Math.random()?.toString(36)?.substr(2, 16)}
`}
      </pre>
    </div>
  );

  return (
    <div className="bg-surface rounded-lg border border-border">
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Report Preview</h3>
          <p className="text-sm text-text-secondary mt-1">Preview your report before generating</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <Button
              variant={previewMode === 'formatted' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPreviewMode('formatted')}
            >
              <Icon name="FileText" size={16} color="currentColor" className="mr-2" />
              Formatted
            </Button>
            <Button
              variant={previewMode === 'raw' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPreviewMode('raw')}
            >
              <Icon name="Code" size={16} color="currentColor" className="mr-2" />
              Raw
            </Button>
          </div>
          
          {previewMode === 'formatted' && (
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setZoom(Math.max(50, zoom - 25))}
                disabled={zoom <= 50}
              >
                <Icon name="ZoomOut" size={16} color="currentColor" />
              </Button>
              <span className="text-sm text-text-secondary min-w-[60px] text-center">
                {zoom}%
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setZoom(Math.min(200, zoom + 25))}
                disabled={zoom >= 200}
              >
                <Icon name="ZoomIn" size={16} color="currentColor" />
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="p-6">
        <div className="overflow-auto max-h-[800px]">
          {previewMode === 'formatted' ? renderFormattedPreview() : renderRawPreview()}
        </div>
      </div>

      <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
        <div className="text-sm text-text-secondary">
          <Icon name="Info" size={16} color="currentColor" className="inline mr-2" />
          This is a preview. Final report may vary based on export format.
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" size="sm">
            <Icon name="Download" size={16} color="currentColor" className="mr-2" />
            Export PDF
          </Button>
          <Button variant="outline" size="sm">
            <Icon name="FileSpreadsheet" size={16} color="currentColor" className="mr-2" />
            Export CSV
          </Button>
          <Button variant="default" size="sm">
            <Icon name="Share" size={16} color="currentColor" className="mr-2" />
            Share Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportPreview;